import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Typography, Chip, CircularProgress, Select, MenuItem, IconButton, Tooltip, Button,
    TextField, InputAdornment,
} from '@material-ui/core';
import { CheckCircle, Visibility, Search } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { WhatsappTemplatePreview } from '../../../components/WhatsappTemplatePreview/WhatsappTemplatePreview';
import { WaTemplateItem } from '../../../Models/DataSources/SmartSendWhatsApp';
import {
    getWaTemplates, selectWaTemplate, setWaFromNumber,
} from '../../../redux/reducers/smartSendWhatsAppSlice';
import { selectWaAllowed, selectWaEnabled, WaRootState } from './selectors';
import { pinnedOrder } from '../pinnedOrder';

// SmartSend part B — step 3 of the picker: which APPROVED template goes out. Full width, under
// the campaign grid, rendered only while the add-on tile is on.
//
// 🔴 WHICH TEMPLATES ARE OFFERED, AND WHY THE TWO EXCLUSIONS ARE NOT SYMMETRIC (v1.20 §2.1):
//  • MEDIA templates are IN the MVP. The disabling that used to sit on `HasMedia` is gone — it
//    never had a technical reason, only a budget one, and the server now carries `MediaUrl` /
//    `MediaType` end to end. The chip stays as a DESCRIPTION of the template, not a refusal.
//  • `ButtonVarUnresolved` and `IsContiguous === false` STAY disabled, and each has a concrete
//    mechanism behind it: the sender can only fill a URL-button `{{n}}` whose
//    `$.variables."n"` is `freetextwebsiteencid` (it returns NULL for anything else, and the
//    parameter simply goes missing), and step 4 of §3.4 validates `1..PlaceholderCount` only,
//    so a numbering gap fails at SEND time — after the charge — instead of here.
//
// 🔴 C-24 (owner decision, mock approved by the client 2026-09-06) — CARDS, NOT AN AUTOCOMPLETE.
// This screen picks things by CARD three times over — the channel tile, the campaign grid, the
// source grid — and all three share one treatment: 2px #e0e0e0, radius 8, selected = primary
// border plus a 2% wash, disabled = faded. A combobox here was the odd one out, and it hid the
// two facts the operator needs to compare templates (language, variable count) behind a click.
// The mock is the visual spec; the grid geometry below is `CampaignPicker.tsx:118-141` verbatim.
//
// ⚠️ AND IT KEEPS WHAT THE AUTOCOMPLETE GOT RIGHT: an unusable template is still RENDERED, still
// says WHY, and is simply not selectable. Silently omitting it produces a support call, not a
// decision — `getOptionDisabled` existed for that reason and the card's `aria-disabled` carries
// it now.

// 🔴 C-36 — THE CAP, AND WHY IT IS NINE AND NOT THE SIBLING'S EIGHT.
// `CampaignPicker` caps at 8 because its grid is FOUR columns and 8 is two whole rows; its own
// note explains that a wrapping flex row made the cap correspond to a ragged number of rows.
// This grid is THREE columns (C-24, the mock's `.cardgrid`), so the same reasoning gives 9.
// The account this shipped against has 37 approved templates rendered at once.
//
// ⚠️ AND THE CAP MUST NOT UNDO WHAT C-24 DECIDED: a template that cannot be used stays
// RENDERED AND DISABLED WITH A REASON. The cap holds back a TAIL — blocked or not, in list
// order — exactly as the campaign grid does, and "show more" reveals it. That is a control the
// operator can press, not a silent omission; "השמטה שקטה מייצרת פנייה לתמיכה, לא החלטה".
// 8, and four across — the same as CampaignPicker (`:80`, `:120`). Owner call 21.09: three
// grids on one screen must not each pick their own page size and their own card width.
const CAP = 8;

const useStyles = makeStyles((theme) => ({
    wrap: { marginTop: theme.spacing(3) },
    // Centred, neutral, OUTLINED — the sibling's `moreBar`. Deliberately not primary-coloured:
    // it reveals more of the same list, it does not change what the list is.
    moreBar: { display: 'flex', justifyContent: 'center', marginTop: theme.spacing(2) },
    search: { minWidth: 260, flex: '0 1 320px' },
    empty: { marginTop: theme.spacing(2), textAlign: 'center' },
    row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(2), marginTop: theme.spacing(1) },
    fromNumber: { minWidth: 200 },
    // The grid geometry is `CampaignPicker.tsx:118-126`, with three columns instead of four —
    // the mock's `.cardgrid` — because a template card carries a long human name plus two facts
    // and goes ragged at a quarter width. `minmax(0, 1fr)` (not `1fr`) is load-bearing: grid
    // items default to `min-content`, so one unbroken template name would otherwise force its
    // track wider than its share and push the others off.
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
        [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    card: {
        position: 'relative', boxSizing: 'border-box', minWidth: 0, padding: theme.spacing(1.5),
        border: '2px solid var(--pls-d-border, #e0e0e0)', borderRadius: 8, cursor: 'pointer', outline: 'none',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: theme.palette.primary.light },
        '&:focus, &:focus-visible': { boxShadow: `0 0 0 3px ${theme.palette.primary.light}` },
    },
    cardOn: { borderColor: theme.palette.primary.main, background: 'rgba(0, 0, 0, 0.02)' },
    // 0.6, not the channel tile's 0.55: these cards carry a REASON chip that has to stay
    // readable — a disabled card whose explanation cannot be read is a dead end.
    cardOff: {
        cursor: 'not-allowed', opacity: 0.6,
        '&:hover': { borderColor: 'var(--pls-d-border, #e0e0e0)' },
        '&:focus, &:focus-visible': { boxShadow: 'none' },
    },
    // ⚠️ `paddingInlineEnd` IS NOT DECORATION — IT IS WHAT MAKES ROOM FOR THE TICK.
    // `overflowWrap:'anywhere'` lets a long friendly name break mid-word and run straight under
    // the absolutely-positioned tick, which takes no space of its own. The same reserve
    // CampaignPicker.tsx:142 makes on its own title row, for the same tick.
    cardTitle: { fontWeight: 600, overflowWrap: 'anywhere', paddingInlineEnd: theme.spacing(3) },
    // 🟣 C-38 · PURPLE (`color="primary"`), NOT the channel green: green = "this is a channel",
    // purple = "this is content" (owner, 16.09), and a template is content. The 15.09 demo mock
    // drew this one green — the owner decided otherwise, and the ledger records the divergence.
    // Geometry copied from CampaignPicker.tsx:172 / SourcePicker.tsx:55; `card` already carries
    // `position:'relative'` (`:78`), so the anchor costs nothing.
    check: { position: 'absolute', top: 8, insetInlineEnd: 8 },
    meta: { marginTop: theme.spacing(0.5) },
    chips: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1), marginTop: theme.spacing(1) },
    state: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: theme.spacing(1) },
    srOnly: {
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
    },
}));

// The one definition of "this template cannot be used". Kept here rather than inline so the
// card's selectability, its reason chip and its screen-reader description all read the same
// rule — and so the mapping tab, which imports it, cannot disagree with the picker.
export const templateBlockReason = (tpl: WaTemplateItem): 'buttonVarUnresolved' | 'notContiguous' | null => {
    if (tpl.ButtonVarUnresolved) return 'buttonVarUnresolved';
    if (tpl.IsContiguous === false) return 'notContiguous';
    return null;
};

interface Props {
    /** The app-wide sheet (App.js:733). Needed by `WhatsappTemplatePreview`, which renders the
     *  live `WhatsappMobilePreview` and reads `whatsappStyles.js` classes off it. */
    classes: any;
    /**
     * 🔴 C-44 · THE LEG OF THE **SELECTED** CAMPAIGN, HANDED DOWN — never read from the slice.
     * Identical in shape and in reason to `WaAddonCard`'s `leg` prop (C-39): the slice keeps the
     * PREVIOUS campaign's leg until the next `GET Mapping` answer lands, and this flag turns
     * EVERY template card read-only at once. Read raw, campaign A's leg locked campaign B's
     * picker, and the operator had no way to choose a template for a campaign that has none.
     *
     * `null` means "no leg for this selection", INCLUDING "we do not know yet" — the safe
     * reading, and the same one the tile takes: it leaves the grid live, the server still
     * refuses what it should refuse, and `getWaMapping.fulfilled` still wins on WHICH template.
     * The opposite default LOCKS a campaign that is fine, with nothing on screen to explain it.
     */
    leg?: { HasLeg?: boolean } | null;
}

const WhatsAppTemplatePicker: React.FC<Props> = ({ classes: appClasses, leg = null }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const allowed = useSelector((s: WaRootState) => selectWaAllowed(s));
    const enabled = useSelector((s: WaRootState) => selectWaEnabled(s));
    const withWhatsApp = useSelector((s: any) => s.smartSendWa.withWhatsApp);
    const templates: WaTemplateItem[] = useSelector((s: any) => s.smartSendWa.waTemplates);
    const templatesStatus = useSelector((s: any) => s.smartSendWa.templatesStatus);
    const chosenId: string | null = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const fromNumber: string | null = useSelector((s: any) => s.smartSendWa.whatsAppFromNumber);
    const availability = useSelector((s: any) => s.smartSendWa.waAvailability);
    // A leg already exists (the operator came back to a campaign that is already mapped). The
    // template is then NOT re-selectable here: "server > URL > slice" means a different
    // `?waTemplate=` is dropped silently, so offering the choice would let the operator pick B
    // and be shown A (v1.8 R5 J3-4). Changing it is "הסר וואטסאפ" then pick again.
    // From the PROP, campaign-guarded by the parent — see `leg` above (C-44).
    const hasLeg = !!leg?.HasLeg;

    const [previewId, setPreviewId] = useState<string | null>(null);
    // The cap expander. Declared with the other hooks, above the early return below — it is a
    // hook, and a hook after a conditional return is a crash waiting for the first empty list.
    const [expanded, setExpanded] = useState(false);
    // C-37 · client-side filtering, and the reason is the sibling's, unchanged: the server does
    // not search these, and the whole approved list is already in memory — a round trip would
    // buy nothing but latency.
    const [search, setSearch] = useState('');
    const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
    // QA BUG-34 — the template pinned first in the capped grid (see `capped`). Declared with the hooks.
    const pinRef = useRef<string | null | undefined>(undefined);

    const active = allowed && enabled && withWhatsApp;

    useEffect(() => {
        if (active && templatesStatus === 'idle') dispatch(getWaTemplates());
    }, [active, templatesStatus, dispatch]);

    // `useMemo`, not a bare `??`: the fallback allocates a NEW array on every render, and this
    // value is a dependency of the effect below — so the effect re-ran on every render rather
    // than when the connected numbers actually changed.
    const numbers = useMemo(() => availability?.FromNumbers ?? [], [availability]);

    // ONE connected number ⇒ no control at all, it is simply used (05 §7 trim). A control with a
    // single option is a decision the operator cannot make.
    useEffect(() => {
        if (!active) return;
        if (fromNumber == null && numbers.length === 1) dispatch(setWaFromNumber(numbers[0].FromNumber));
    }, [active, fromNumber, numbers, dispatch]);

    if (!active) return null;

    // A template is pickable unless it is blocked, and — once a leg exists — unless the whole
    // control is read-only. `hasLeg` is not a per-card property, so it gates every card at once.
    const pickable = (tpl: WaTemplateItem) => !hasLeg && templateBlockReason(tpl) == null;

    // C-37 · the visible set, before the cap. `FriendlyTemplateName` is the name the operator
    // reads on the card, with `TemplateName` behind it for a template that has no friendly one —
    // searching a field that is not on screen finds things the operator cannot see.
    const matches = (() => {
        const term = search.trim().toLowerCase();
        if (!term) return templates;
        return templates.filter((tp) => (
            `${tp.FriendlyTemplateName ?? ''} ${tp.TemplateName ?? ''}`.toLowerCase().indexOf(term) > -1
        ));
    })();

    // 🔴 C-36 · WHAT THE GRID ACTUALLY RENDERS. Cards past the cap stay OUT OF THE DOM — and so
    // out of the roving-tabindex and arrow order below — until "show more" is pressed.
    // QA BUG-34 (Idan) — the template chosen when the list first arrived (the URL's `?waTemplate=`, or
    // one kept in the slice) is shown FIRST and the grid stays capped (`pinnedOrder`), replacing the old
    // exception "the tab-stop card sorts past the cap ⇒ reveal the whole list". With nothing pinned in
    // the matches, the first pickable card is pinned the same way, so the collapsed grid always holds a
    // card that can take the tab stop. Taken ONCE per mount (when the list has loaded), never from a
    // later click, so a card picked after «הצג עוד» does not jump. A ref written once during render.
    if (pinRef.current === undefined && templatesStatus === 'succeeded') pinRef.current = chosenId ?? null;
    const pinnedId = pinRef.current ?? null;
    const capped = (() => {
        const ordered = pinnedOrder(matches, (tp) => tp.TemplateId === pinnedId, pickable, CAP);
        return expanded ? ordered : ordered.slice(0, CAP);
    })();
    const moreCount = matches.length - capped.length;

    // ⚠️ EVERY INDEX-BASED HELPER IS COMPUTED OVER `capped`, NEVER OVER `templates` — otherwise
    // the indices do not line up with the rendered cards and the roving tab stop lands on a card
    // that is not there.
    const pickableIdx = capped.reduce<number[]>(
        (acc, tpl, i) => (pickable(tpl) ? [...acc, i] : acc), []);
    // Roving tabindex: exactly one card is tabbable — the chosen one, else the first pickable.
    const chosenIdx = capped.findIndex((tpl) => tpl.TemplateId === chosenId && pickable(tpl));
    const rovingTarget = chosenIdx >= 0 ? chosenIdx : (pickableIdx.length ? pickableIdx[0] : -1);

    const pick = (tpl: WaTemplateItem) => {
        if (!pickable(tpl)) return;
        dispatch(selectWaTemplate(tpl));
    };

    // Wrapping RTL grid, same key map as SourcePicker/CampaignPicker: ArrowLeft = next, because
    // the grid flows right-to-left.
    const moveFocus = (fromIdx: number, dir: 1 | -1) => {
        if (!pickableIdx.length) return;
        const pos = pickableIdx.indexOf(fromIdx);
        const next = pickableIdx[(pos + dir + pickableIdx.length) % pickableIdx.length];
        cardRefs.current[next]?.focus();
    };
    const onCardKeyDown = (e: React.KeyboardEvent, idx: number, tpl: WaTemplateItem) => {
        if (!pickable(tpl)) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(tpl); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); moveFocus(idx, 1); }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); moveFocus(idx, -1); }
    };

    return (
        <Box className={classes.wrap}>
            <Typography variant="overline" color="textSecondary" display="block">
                {t('DataSources.send.whatsapp.picker.template')}
            </Typography>

            <Box className={classes.row}>
                {/* Two or more connected numbers ⇒ the operator chooses. One ⇒ no control (the
                    effect above picks it). Zero ⇒ `Availability.Ready` is already false and the
                    tile is blocked with PHONE_NOT_CONNECTED, so this cannot be reached empty. */}
                {numbers.length > 1 && (
                    <Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                            {t('DataSources.send.whatsapp.picker.fromNumber')}
                        </Typography>
                        <Select
                            className={classes.fromNumber}
                            variant="outlined"
                            margin="dense"
                            displayEmpty
                            disabled={hasLeg}
                            value={fromNumber ?? ''}
                            // FU-06A r2 — the chosen number shown left-to-right in an RTL page (the menu already is).
                            // Only a CONNECTED number is shown: one kept from an earlier visit and since disconnected
                            // renders empty, as before, so the operator sees that a number must be chosen.
                            renderValue={(v: any) => (v && numbers.some((n: any) => n.FromNumber === v) ? <bdi dir="ltr">{v}</bdi> : '')}
                            onChange={(e: any) => dispatch(setWaFromNumber(e.target.value || null))}
                            inputProps={{ 'aria-label': t('DataSources.send.whatsapp.picker.fromNumber') }}
                            MenuProps={{ PaperProps: { dir: 'ltr' } }}
                        >
                            {numbers.map((n: any) => (
                                <MenuItem key={n.FromNumber} value={n.FromNumber}>{n.FromNumber}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}
            </Box>

            {/* C-37 · the search box. Rendered only once there is something to search — a filter
                over a list the account does not have is furniture, and the "no templates
                approved" line below already says what is wrong in that case. */}
            {templates.length > 0 && (
                <Box className={classes.row}>
                    <TextField
                        className={classes.search}
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e: any) => setSearch(e.target.value)}
                        placeholder={t('DataSources.send.whatsapp.picker.searchTemplate')}
                        inputProps={{ 'aria-label': t('DataSources.send.whatsapp.picker.searchTemplate') }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}

            {templatesStatus === 'loading' && (
                <Box className={classes.state}><CircularProgress size={18} /></Box>
            )}
            {templatesStatus === 'succeeded' && templates.length === 0 && (
                <Typography variant="body2" color="textSecondary" className={classes.state}>
                    {t('DataSources.send.whatsapp.picker.noTemplates')}
                </Typography>
            )}

            {/* ⚠️ THE TRAP `CampaignPicker` ALREADY SOLVED (`:545-577`): "no results" must never
                appear over an EMPTY search box. The two dead ends look identical on screen and
                have opposite escapes — here the escape is clearing the text, and it is only
                offered when there IS text to clear. With no search term and no templates the
                account simply has none, which `picker.noTemplates` above already says. */}
            {templates.length > 0 && matches.length === 0 && (
                <Box className={classes.empty}>
                    <Typography variant="body2" color="textSecondary">
                        {t('DataSources.send.whatsapp.picker.noTemplateResults')}
                    </Typography>
                    <Button size="small" color="primary" onClick={() => setSearch('')}>
                        {t('DataSources.send.picker.clearSearch')}
                    </Button>
                </Box>
            )}

            {matches.length > 0 && (
                <Box
                    className={classes.grid}
                    role="radiogroup"
                    aria-label={t('DataSources.send.whatsapp.picker.template')}
                >
                    {capped.map((tpl, idx) => {
                        const reason = templateBlockReason(tpl);
                        const isChosen = tpl.TemplateId === chosenId;
                        const live = pickable(tpl);
                        const descId = `smartsend-wa-tpl-${tpl.TemplateId}`;
                        // Everything the card shows, collected into ONE sr-only node:
                        // `aria-label` sets the accessible NAME and thereby suppresses the card's
                        // inner text, so the language, the variable count and the reason would
                        // otherwise be announced to nobody. Same treatment as SourcePicker.
                        const description = [
                            t('DataSources.send.whatsapp.picker.card.language', { lang: tpl.Language ?? '' }),
                            t('DataSources.send.whatsapp.picker.card.placeholders', { n: (tpl.PlaceholderCount ?? 0).toLocaleString() }),
                            (tpl.HasMedia || !!tpl.MediaUrl) ? t('DataSources.send.whatsapp.picker.card.media') : null,
                            reason ? t(`DataSources.send.whatsapp.picker.card.${reason}`) : null,
                        ].filter(Boolean).join('. ');

                        return (
                            <div
                                key={tpl.TemplateId}
                                ref={(el) => { cardRefs.current[idx] = el; }}
                                className={clsx(classes.card, isChosen && classes.cardOn, !live && classes.cardOff)}
                                role="radio"
                                aria-checked={isChosen}
                                aria-disabled={live ? undefined : true}
                                aria-label={tpl.FriendlyTemplateName || tpl.TemplateName || ''}
                                aria-describedby={descId}
                                tabIndex={live ? (idx === rovingTarget ? 0 : -1) : -1}
                                onMouseDown={live ? undefined : (e) => e.preventDefault()}
                                onClick={() => pick(tpl)}
                                onKeyDown={(e) => onCardKeyDown(e, idx, tpl)}
                            >
                                {/* 🟣 C-38 · the chosen card says so the way the two sibling grids
                                    already say it (CampaignPicker.tsx:459, SourcePicker.tsx:553).
                                    This grid was the exception, not the rule — a border tint and a
                                    2% wash were the ONLY difference between chosen and not.
                                    🔵 a11y: `aria-checked={isChosen}` (`:345`) already carries the
                                    state and CheckCircle renders `aria-hidden` — decoration only. */}
                                {isChosen && <CheckCircle className={classes.check} color="primary" fontSize="small" />}
                                <Typography className={classes.cardTitle}>
                                    {tpl.FriendlyTemplateName || tpl.TemplateName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" className={classes.meta}>
                                    {t('DataSources.send.whatsapp.picker.card.language', { lang: tpl.Language ?? '' })}
                                    {' · '}
                                    {t('DataSources.send.whatsapp.picker.card.placeholders', { n: (tpl.PlaceholderCount ?? 0).toLocaleString() })}
                                </Typography>
                                <Box className={classes.chips}>
                                    <Chip size="small" color="primary" variant="outlined"
                                        label={t('DataSources.send.whatsapp.picker.card.approved')} />
                                    {/* DESCRIPTIVE. Media templates are in the MVP (v1.20 §2.1) —
                                        this chip no longer disables anything. 2026-10-08 WA-MEDIA:
                                        also on MediaUrl, so a `card` template with a header image
                                        (HasMedia false) is labelled as carrying media. */}
                                    {(tpl.HasMedia || !!tpl.MediaUrl) && (
                                        <Chip size="small" variant="outlined"
                                            label={t('DataSources.send.whatsapp.picker.card.media')} />
                                    )}
                                    {reason && (
                                        <Chip size="small" variant="outlined"
                                            label={t(`DataSources.send.whatsapp.picker.card.${reason}`)} />
                                    )}
                                    {/* 👁 ON EVERY CARD — AND THE COMMENT THAT STOOD HERE WAS WRONG.
                                        It said "the mock shows it there and nowhere else". The mock
                                        does the opposite: in `tplCardHtml` the eye is appended
                                        UNCONDITIONALLY and only the ✓ is gated on `if (on)`.
                                        Measured in Clal-WhatsApp-Demo-Screens.html, not inferred.
                                        The behaviour it costs is the real one: with the eye on the
                                        chosen card only, comparing two candidates means PICKING one
                                        to read it — and picking a different template clears the
                                        working variable map (selectWaTemplate in the slice).
                                        ⚠️ `stopPropagation`: the button sits INSIDE a radio, and
                                        without it opening the preview would also pick the
                                        template — a click that does two things, one of them
                                        unasked. */}
                                    {(
                                        <Tooltip title={t('DataSources.send.whatsapp.picker.preview') as string}>
                                            <IconButton
                                                size="small"
                                                // 🔴 `TemplateId` (the ContentSid string), NOT the
                                                // numeric `Id` (v1.4 T-27) — that is the id every
                                                // WhatsApp/* route speaks.
                                                onClick={(e) => { e.stopPropagation(); setPreviewId(tpl.TemplateId); }}
                                                aria-label={t('DataSources.send.whatsapp.picker.preview')}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <span id={descId} className={classes.srOnly}>{description}</span>
                            </div>
                        );
                    })}
                </Box>
            )}

            {/* C-36 · the cap expander. `moreCount` is how many templates the cap is still
                holding back; it disappears once expanded or when the list already fits. The key
                is the sibling's own `picker.showMore` — channel-neutral ("הצג עוד {{count}}"),
                already in three languages, and NOT part of §7's `send.whatsapp.*` group, so it
                adds nothing to the mandatory list. */}
            {moreCount > 0 && (
                <Box className={classes.moreBar}>
                    <Button size="small" variant="outlined" onClick={() => setExpanded(true)}>
                        {t('DataSources.send.picker.showMore', { count: moreCount.toLocaleString() })}
                    </Button>
                </Box>
            )}

            <WhatsappTemplatePreview
                classes={appClasses}
                templateID={previewId ?? ''}
                openPreview={previewId != null}
                closeModel={() => setPreviewId(null)}
            />
        </Box>
    );
};

export default WhatsAppTemplatePicker;
